import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

const SCRIPT_ID = "recaptcha-enterprise-script";
const SCRIPT_SRC =
  "https://www.google.com/recaptcha/enterprise.js?render=explicit";

let scriptLoadPromise = null;

function loadRecaptchaScript() {
  if (window.grecaptcha?.enterprise) {
    return Promise.resolve();
  }

  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Failed to load reCAPTCHA")),
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error("Failed to load reCAPTCHA"));
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

const RecaptchaCheckbox = forwardRef(function RecaptchaCheckbox(
  { action, onTokenChange },
  ref,
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenChangeRef = useRef(onTokenChange);
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  onTokenChangeRef.current = onTokenChange;

  useImperativeHandle(ref, () => ({
    reset() {
      if (widgetIdRef.current !== null && window.grecaptcha?.enterprise) {
        window.grecaptcha.enterprise.reset(widgetIdRef.current);
      }
      onTokenChangeRef.current?.("");
    },
    getToken() {
      if (widgetIdRef.current !== null && window.grecaptcha?.enterprise) {
        return window.grecaptcha.enterprise.getResponse(widgetIdRef.current);
      }
      return "";
    },
  }));

  useEffect(() => {
    if (!siteKey || !containerRef.current) {
      return undefined;
    }

    let cancelled = false;

    const notify = (token) => {
      onTokenChangeRef.current?.(token || "");
    };

    const renderWidget = () => {
      if (cancelled || !containerRef.current || !window.grecaptcha?.enterprise) {
        return;
      }
      if (widgetIdRef.current !== null) {
        return;
      }

      widgetIdRef.current = window.grecaptcha.enterprise.render(
        containerRef.current,
        {
          sitekey: siteKey,
          action,
          callback: (token) => notify(token),
          "expired-callback": () => notify(""),
          "error-callback": () => notify(""),
        },
      );
    };

    loadRecaptchaScript()
      .then(() => {
        if (cancelled) {
          return;
        }
        if (window.grecaptcha.enterprise.ready) {
          window.grecaptcha.enterprise.ready(renderWidget);
        } else {
          renderWidget();
        }
      })
      .catch((err) => {
        console.error(err);
      });

    return () => {
      cancelled = true;
      widgetIdRef.current = null;
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
  }, [action, siteKey]);

  if (!siteKey) {
    return (
      <p className="text-danger small my-3">
        reCAPTCHA is not configured. Form submissions are blocked until a site
        key is set.
      </p>
    );
  }

  return (
    <div
      className="my-3"
      style={{ overflowX: "auto" }}
      ref={containerRef}
    />
  );
});

export default RecaptchaCheckbox;
